import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, useLocation } from "react-router-dom";
import { Crown, AlertTriangle, Lock, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { tanggalID } from "@/lib/format";

export function subState(sub) {
  if (!sub || !sub.managed) return null;
  if (sub.phase === "locked") return { tone: "rose", icon: Lock, title: "Akun terkunci", msg: "Masa langganan sudah berakhir. Perpanjang untuk membuka kembali akun Anda." };
  if (sub.phase === "read_only") return { tone: "rose", icon: AlertTriangle, title: "Mode baca saja",
    msg: `Langganan berakhir ${tanggalID(sub.expires_at)}. Data hanya bisa dilihat sampai ${tanggalID(sub.grace_until)}, setelah itu akun terkunci.` };
  if (sub.days_left != null && sub.days_left <= (sub.remind_days ?? 7)) return { tone: "amber", icon: Clock,
    title: sub.status === "trial" ? "Masa coba hampir habis" : "Langganan hampir habis",
    msg: `${sub.status === "trial" ? "Masa coba" : "Paket " + sub.plan_name} berakhir dalam ${Math.max(sub.days_left, 0)} hari (${tanggalID(sub.expires_at)}).` };
  return null;
}

const TONE = { rose: "bg-rose-50 border-rose-200 text-rose-800", amber: "bg-amber-50 border-amber-200 text-amber-800" };
const FREE_PATHS = ["/langganan", "/pengaturan/profil"];

export function SubscriptionNotice({ user }) {
  const loc = useLocation();
  const sub = user?.subscription;
  const st = subState(sub);
  const [popup, setPopup] = useState(false);
  useEffect(() => {
    if (!st || sessionStorage.getItem("khn_sub_popup") === sub.phase + sub.expires_at) return;
    setPopup(true);
    sessionStorage.setItem("khn_sub_popup", sub.phase + sub.expires_at);
  }, [st, sub]);
  if (!st) return null;
  if (sub.phase === "locked" && !FREE_PATHS.includes(loc.pathname)) return <Navigate to="/langganan" replace/>;
  const Icon = st.icon;
  return (
    <>
      {loc.pathname !== "/langganan" && (
        <div className={`border-b px-4 md:px-8 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm ${TONE[st.tone]}`} data-testid="sub-banner">
          <Icon size={16} className="shrink-0"/>
          <span className="font-semibold">{st.title}.</span>
          <span className="flex-1 min-w-[200px]">{st.msg}</span>
          <Link to="/langganan" className="font-semibold underline underline-offset-2" data-testid="sub-banner-cta">Perpanjang / Upgrade</Link>
        </div>
      )}
      <Dialog open={popup} onOpenChange={setPopup}>
        <DialogContent className="max-w-md" data-testid="sub-popup">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Icon size={18}/> {st.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{st.msg}</p>
          <DialogFooter data-dialog-footer>
            <Button variant="outline" onClick={() => setPopup(false)} data-testid="sub-popup-later">Nanti</Button>
            <Button asChild className="brand-btn" onClick={() => setPopup(false)}><Link to="/langganan" data-testid="sub-popup-cta">Lihat Paket</Link></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const SubLink = ({ onNav, user }) => (
  <NavLink to="/langganan" onClick={onNav} data-testid="nav-langganan"
    className={({ isActive }) => `side-link flex items-center justify-between gap-3 px-3 py-2 rounded-md text-[13.5px] ${isActive ? "active text-white" : "text-slate-300"}`}>
    <span className="flex items-center gap-3"><Crown size={16}/> Langganan</span>
    {user?.subscription?.managed && <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-slate-200 truncate max-w-[96px]">{user.subscription.plan_name}</span>}
  </NavLink>
);
